import React from 'react';
import { Card, Table, Badge, Row, Col, Accordion } from 'react-bootstrap';
import { FaBox, FaWarehouse, FaBoxOpen, FaClock, FaStickyNote } from 'react-icons/fa';

const StoredProductsDisplay = ({ storedProducts }) => {
  // Handle undefined, null, or empty storedProducts
  if (!storedProducts || !storedProducts.products || storedProducts.products.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center py-5">
          <FaBoxOpen size={50} className="text-muted mb-3" />
          <h6 className="text-muted">No Stored Products Yet</h6>
          <p className="text-muted mb-0 small">
            Click "Store Finished Product" button above to add storage entries
          </p>
        </Card.Body>
      </Card>
    );
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (stored, total) => {
    return total > 0 ? Math.min(100, (stored / total) * 100) : 0;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <FaBox className="me-2 text-primary" />
          Stored Products
        </h5>
        <Badge bg="primary" pill>
          {storedProducts.totalEntries} {storedProducts.totalEntries === 1 ? 'Entry' : 'Entries'}
        </Badge>
      </div>

      {storedProducts.products.map((productData, idx) => {
        const progressPercentage = getProgressPercentage(
          productData.totalStored,
          productData.totalCalculated
        );

        return (
          <Card key={idx} className="mb-3 border-0 shadow-sm">
            <Card.Header className="bg-gradient-primary text-white">
              <Row className="align-items-center">
                <Col md={3}>
                  <h6 className="mb-0">
                    <FaBoxOpen className="me-2" />
                    {productData.product.productName}
                  </h6>
                </Col>
                <Col md={9}>
                  <Row>
                    <Col md={4} className="text-center">
                      <small className="d-block opacity-75">Total Stored</small>
                      <strong className="fs-8">
                        {productData.totalStored.toFixed(2)} {productData.unit}
                      </strong>
                    </Col>
                    <Col md={4} className="text-center">
                      <small className="d-block opacity-75">Total Bags</small>
                      <strong className="fs-8">
                        {productData.totalBags} bags
                      </strong>
                    </Col>
                    <Col md={4} className="text-center">
                      <small className="d-block opacity-75">Progress</small>
                      <strong className="fs-8">
                        {progressPercentage.toFixed(0)}%
                      </strong>
                    </Col>
                  </Row>
                </Col>
              </Row>
              
              {/* Progress Bar */}
              <div className="mt-2">
                <div className="progress" style={{ height: '8px' }}>
                  <div
                    className={`progress-bar ${
                      progressPercentage === 100 ? 'bg-success' : 'bg-warning'
                    }`}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <small className="opacity-75">
                  {productData.totalStored.toFixed(2)} / {productData.totalCalculated.toFixed(2)} {productData.unit}
                </small>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {/* Packing Material Summary */}
              {Object.keys(productData.packingMaterials).length > 0 && (
                <div className="p-3 bg-light border-bottom">
                  <h6 className="mb-2">
                    <FaBox className="me-2 text-primary" />
                    Packing Material Summary
                  </h6>
                  <Row>
                    {Object.values(productData.packingMaterials).map((pmData, pmIdx) => (
                      <Col md={6} lg={4} key={pmIdx} className="mb-2">
                        <Card className="border-primary h-100">
                          <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong className="text-primary">
                                  {pmData.packingMaterial.name}
                                </strong>
                                <div className="small text-muted">
                                  {pmData.packingMaterial.alias}
                                </div>
                              </div>
                              <Badge bg="primary" pill>
                                {pmData.totalBags} bags
                              </Badge>
                            </div>
                            <div className="mt-2">
                              <small className="text-muted">Total Quantity:</small>
                              <div className="fw-bold">
                                {pmData.totalQuantity.toFixed(2)} {productData.unit}
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* Detailed Entries */}
              <Accordion flush>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <FaStickyNote className="me-2" />
                    View Detailed Entries ({productData.entries.length})
                  </Accordion.Header>
                  <Accordion.Body className="p-0">
                    <Table responsive hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Date & Time</th>
                          <th>Quantity</th>
                          <th>Packing Material</th>
                          <th>Qty/Bag</th>
                          <th>Bags</th>
                          <th>Location</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productData.entries.map((entry, entryIdx) => (
                          <tr key={entry._id}>
                            <td>{entryIdx + 1}</td>
                            <td>
                              <small>
                                <FaClock className="me-1 text-muted" />
                                {formatDate(entry.createdAt)}
                              </small>
                            </td>
                            <td>
                              <strong>
                                {entry.quantity.toFixed(2)} {entry.unit}
                              </strong>
                            </td>
                            <td>
                              {entry.packingMaterialId ? (
                                <div>
                                  {entry.outerPackingMaterialId ? (
                                    <>
                                      <strong>{entry.outerPackingMaterialId.name}</strong> ({entry.outerNumberOfBags} outer bags)
                                      <div className="ms-3">
                                        └─ Contains: <strong>{entry.packingMaterialId.name}</strong> ({entry.numberOfBags} inner bags, {entry.bagsPerOuterBag.toFixed(2)} per outer bag)
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <strong>{entry.packingMaterialId.name}</strong> ({entry.numberOfBags} bags)
                                    </>
                                  )}
                                  <div className="small text-muted">
                                    {entry.packingMaterialId.alias} (Size: {entry.packingMaterialId.size})
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {entry.quantityPerBag ? (
                                <Badge bg="info">
                                  {entry.quantityPerBag.toFixed(2)} {entry.unit}
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {entry.numberOfBags ? (
                                <Badge bg="primary" pill>
                                  <FaBox className="me-1" />
                                  {entry.numberOfBags}
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <small>
                                <FaWarehouse className="me-1 text-muted" />
                                {entry.warehouseLocationId?.fullPath || 
                                 entry.warehouseLocationId?.label || 
                                 'N/A'}
                              </small>
                            </td>
                            <td>
                              {entry.notes ? (
                                <small className="text-muted">{entry.notes}</small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="2" className="text-end">
                            <strong>Totals:</strong>
                          </td>
                          <td>
                            <strong>
                              {productData.totalStored.toFixed(2)} {productData.unit}
                            </strong>
                          </td>
                          <td colSpan="2"></td>
                          <td>
                            <strong>
                              <FaBox className="me-1" />
                              {productData.totalBags} bags
                            </strong>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Card.Body>
          </Card>
        );
      })}

      <style jsx>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .opacity-75 {
          opacity: 0.75;
        }
      `}</style>
    </div>
  );
};

export default StoredProductsDisplay;
